'use client'

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { BarChart3, Newspaper, Users, PlusCircle, ChevronDown, Sparkles, Smile, RefreshCw } from 'lucide-react'
import { MediaClipsTable } from '@/app/media-clips/components/MediaClipsTable' // Check if this path is correct
import ActivityGraph from '@/components/features/ActivityGraph/ActivityGraph'
import { type SentimentCount } from '@/components/features/ActivityGraph/types' // Import SentimentCount type
import { getSentimentColorValue } from '@/components/features/ActivityGraph/utils/styleUtils' // Import color util
import { SentimentPieChart } from '@/components/features/ActivityGraph/components/SentimentPieChart' // Import Pie Chart
import MainLayout from '@/components/layout/MainLayout'
import { useAuth } from '@/contexts/AuthContext'
import { redirect, useRouter, useSearchParams, usePathname } from 'next/navigation'
import { fetchNewsClippings, BrandAccessFilter, fetchBrandNamesByIds, type NewsClipping, fetchBrands } from '@/utils/supabase-queries'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useFilterState } from '@/hooks/useFilterState'
import { usePersistentState, usePersistentReducer } from '@/hooks/usePersistentState'
import { useHydrationSafe } from '@/hooks/useHydrationSafe'
import { Checkbox } from '@/components/ui/checkbox'
import { SideFilterBar } from '@/components/shared/SideFilterBar'
import { SideFilterSheet, MobileFilterBar } from '@/app/media-clips/components/SideFilterSheet'
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'
import { Loader2 } from 'lucide-react'
import { debounce, processInChunks, memoize } from '@/lib/performance'
import dynamic from 'next/dynamic'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet"
import ClipDetails from '@/app/media-clips/components/ClipDetails' // Check if this path is correct
import { applyFilters as applyDashboardFiltersUtil, extractFilterOptions } from '@/app/media-clips/utils/filterUtils'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card" // Path should be correct now
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useTabVisibility } from '@/hooks/useTabVisibility'
// Note: We use local logo components to avoid individual requests

import { generateFilterCounts } from '@/app/media-clips/utils/filterCountsGenerator'
import { getDynamicFilterCounts } from '@/utils/dynamic-filter-counts'
import { ShareOfVoiceTreemap } from '@/components/features/ShareOfVoiceTreemap'
import { ShareOfVoiceReach } from '@/components/features/ShareOfVoiceReach'

// Utility function to format numbers with thousand separators
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Local BrandLogo component that uses the logo map instead of making individual requests
const BrandLogo = ({ 
  brandName, 
  logoMap 
}: { 
  brandName: string | undefined;
  logoMap: Record<string, string | null>;
}) => {
  if (!brandName) {
    return (
      <svg 
        className="w-3.5 h-3.5 mr-1.5 text-gray-400" 
        viewBox="0 0 24 24" 
        fill="currentColor"
        aria-label="Brand placeholder"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
      </svg>
    );
  }

  const logoUrl = logoMap[brandName];
  
  if (!logoUrl) {
    return (
      <svg 
        className="w-3.5 h-3.5 mr-1.5 text-gray-400" 
        viewBox="0 0 24 24" 
        fill="currentColor"
        aria-label={`Placeholder for ${brandName} logo`}
      >
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
      </svg>
    );
  }

  return (
    <img 
      src={logoUrl}
      alt={`${brandName} logo`} 
      className="w-3.5 h-3.5 mr-1.5 object-contain"
      onError={(e) => {
        // Replace with placeholder on error
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        target.nextElementSibling?.classList.remove('hidden');
      }}
    />
  );
};

// Local MediaOutletLogo component that uses the logo map instead of making individual requests
const MediaOutletLogo = ({ 
  outletName, 
  logoUrl 
}: { 
  outletName: string | undefined;
  logoUrl?: string | null;
}) => {
  if (!outletName || !logoUrl) {
    return (
      <svg 
        className="w-3.5 h-3.5 mr-1.5 text-gray-400" 
        viewBox="0 0 24 24" 
        fill="currentColor"
        aria-label={`Placeholder for ${outletName || 'outlet'} logo`}
      >
        <path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"></path>
      </svg>
    );
  }

  return (
    <img 
      src={logoUrl}
      alt={`${outletName} logo`} 
      className="w-3.5 h-3.5 mr-1.5 object-contain"
      onError={() => {
        // Show placeholder on error - handled by parent
      }}
    />
  );
};

type PeriodType = 'Monthly' | 'Weekly' | 'Daily' | 'Quarterly'

// Define type for Top List items including logo
type TopListItem = {
  name: string;
  count: number;
  logoUrl?: string | null; // Optional logo URL
}

// Global event for opening chat with a specific message
declare global {
  interface WindowEventMap {
    'open-chat-with-message': CustomEvent<{ message: string }>
  }
}

// Add a custom auth checking component
const AuthCheck = () => {
  // Rely solely on AuthContext state
  const { user, loading } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const [redirectAttempted, setRedirectAttempted] = useState(false)

  const logAuth = (message: string) => {
    console.log(`[AuthCheck] ${message} | user: ${!!user} | loading: ${loading} | redirectAttempted: ${redirectAttempted}`);
  };

  useEffect(() => {
    // Check for bypass mode
    const isAuthBypassed = typeof window !== 'undefined' && (
      localStorage.getItem('auth_bypass_active') === 'true' ||
      (window as any).forceDashboardRender === true // Use type assertion
    );

    if (isAuthBypassed) {
      logAuth('AUTH BYPASS MODE DETECTED - Skipping auth check');
      return;
    }

    // If context is loading, wait
    if (loading) {
      logAuth('Auth context is still loading, waiting...');
      return;
    }

    // If context finished loading and there's no user, redirect (only once)
    if (!user && !redirectAttempted) {
      logAuth('No user found after loading, redirecting to signin.');
      setRedirectAttempted(true);
      router.push('/?openAuth=signin&redirect=/dashboard');
    }

  // Dependencies: user, loading, router, redirectAttempted
  }, [user, loading, router, redirectAttempted, pathname]);

  // Set a timeout to avoid being stuck in loading state forever
  useEffect(() => {
    // Only set timeout if still loading
    if (!loading) return;

    const timerId = setTimeout(() => {
      if (loading) {
        logAuth('TIMEOUT - Still in loading state after 5 seconds');
        // Force redirect if needed
        if (!user && !redirectAttempted) {
          logAuth('Forcing redirect after timeout');
          setRedirectAttempted(true);
          router.push('/?openAuth=signin&redirect=/dashboard');
        }
      }
    }, 5000);

    return () => clearTimeout(timerId);
  }, [loading, user, redirectAttempted, router]);

  // Show loading state ONLY while AuthContext is loading
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading session...</p>
        </div>
      </div>
    )
  }

  // If loading is done and we have a user, render the dashboard (by returning null)
  // If loading is done and we have no user, redirect is triggered, render null while redirect happens
  return null;
}

// Helper function to get top N items (generic) - now returns TopListItem
const getTopItems = (
  clippings: NewsClipping[], 
  key: keyof NewsClipping, 
  count: number = 7,
  logoMap?: Record<string, string | null> // Optional logo map
): TopListItem[] => {
  if (!clippings || clippings.length === 0) return [];

  const itemCounts = clippings.reduce<Record<string, number>>((acc, clip) => {
    const itemValue = clip[key] as string | undefined; 
    if (itemValue) {
      acc[itemValue] = (acc[itemValue] || 0) + 1;
    }
    return acc;
  }, {});

  return Object.entries(itemCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, count)
    .map(([name, count]) => ({
      name,
      count,
      // Look up logo URL from the map if provided
      logoUrl: logoMap ? logoMap[name] : null
    }));
};

// Fix the KeyMetricsCard component to accept React elements
const KeyMetricsCard = ({ stat, label, icon }: { stat: number | string | React.ReactNode; label: string; icon: React.ReactNode }) => (
  <Card className="shadow h-full">
    <CardContent className="p-3 sm:p-5 flex flex-row items-center justify-between h-full">
      <div className="flex flex-col text-center flex-grow mr-2">
        <p className="text-xl sm:text-2xl font-bold">{stat}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
      <div className="bg-primary/10 rounded-full p-2 sm:p-3 flex-shrink-0">
        {icon}
      </div>
    </CardContent>
  </Card>
);

// Sentiment Analysis Card (Only Pie Chart)
const SentimentAnalysisCard = ({ sentimentData }: { sentimentData: SentimentCount[] }) => {
  return (
    <Card className="shadow h-full sentiment-card">
      <CardHeader className="p-3 sm:p-4">
        <CardTitle className="text-base font-medium">Sentiment Analysis</CardTitle>
      </CardHeader>
      <CardContent className="p-0 sm:p-4 pt-0 flex items-center justify-center h-[240px]">
        {sentimentData.length > 0 ? (
          <div className="flex items-center justify-center w-full h-full mt-[-24px] sm:mt-0 pie-chart-container" style={{ transform: 'scale(1.2)' }}>
            <SentimentPieChart 
              sentimentData={sentimentData} 
              onSentimentClick={() => {}} 
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">Not enough sentiment data.</p>
        )}
      </CardContent>
    </Card>
  );
};

const LatestClipsCard = ({ 
  clips, 
  onClipClick,
  brandLogoMap,
  skipIndividualRequests = false
}: { 
  clips: NewsClipping[]; 
  onClipClick: (id: string) => void;
  brandLogoMap: Record<string, string | null>;
  skipIndividualRequests?: boolean;
}) => {
  // This determines how many clips to show based on screen size
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Show 5 clips on mobile instead of 6
  const displayCount = isMobile ? 5 : 6;
  
  return (
    <Card className="shadow h-full">
      <CardHeader className="p-3 sm:p-4 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium">Latest Clips</CardTitle>
        <Link href="/media-clips" className="text-xs text-primary hover:underline">
          View all
        </Link>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 pt-0 sm:pt-0">
        {clips.length > 0 ? (
          <ul className="space-y-1.5">
            {clips.slice(0, displayCount).map((clip) => {
              return (
              <li
                key={String(clip.id)}
                className="text-sm hover:bg-muted/50 p-1.5 rounded cursor-pointer transition-colors flex items-center space-x-3"
                onClick={() => onClipClick(String(clip.id))}
              >
                {/* Logo/Placeholder */}
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 relative rounded-sm border border-muted overflow-hidden bg-background flex items-center justify-center">
                    {skipIndividualRequests ? (
                      // Show placeholder when individual requests are skipped
                      <svg 
                        className="w-3.5 h-3.5 mr-1.5 text-gray-400" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                        aria-label={`Placeholder for ${clip.brandName} logo`}
                      >
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                      </svg>
                    ) : (
                      <BrandLogo brandName={clip.brandName} logoMap={brandLogoMap} />
                    )}
                  </div>
                </div>
                {/* Clip Details */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate mb-0.5" title={clip.title}>{clip.title || 'Untitled'}</p>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-muted-foreground truncate max-w-[70%]">{clip.source || 'Unknown Source'}</p>
                    <p className="text-xs text-muted-foreground">{clip.date ? new Date(clip.date).toLocaleDateString() : 'No Date'}</p>
                  </div>
                </div>
              </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No recent clippings found.</p>
        )}
      </CardContent>
    </Card>
  );
};

// Updated TopListCard to show logos and make items clickable
const TopListCard = ({ 
  title, 
  items, 
  className = "",
  showViewAll = false,
  onItemClick,
  brandLogoMap,
  skipIndividualRequests = false
}: { 
  title: string; 
  items: TopListItem[]; 
  className?: string;
  showViewAll?: boolean;
  onItemClick?: (name: string) => void;
  brandLogoMap: Record<string, string | null>;
  skipIndividualRequests?: boolean;
}) => {
  // Determine the link based on the title
  const viewAllLink = title === "Top Brands" ? "/brands" : ""; // Empty string for Top Sources
  const isBrandList = title === "Top Brands";

  return (
    <Card className={`shadow h-full ${className}`}>
      <CardHeader className="p-3 sm:p-4 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium">{title}</CardTitle>
        
        {showViewAll && viewAllLink && (
          <Link href={viewAllLink} className="text-xs text-primary hover:underline">
            View all
          </Link>
        )}
      </CardHeader>
      <CardContent className="p-3 sm:p-4 pt-0 sm:pt-0">
        {items.length > 0 ? (
           <ul className="space-y-3">
            {items.map(({ name, count, logoUrl }) => (
              <li 
                key={name} 
                className="flex justify-between items-center text-sm hover:bg-muted/50 rounded p-1.5 cursor-pointer transition-colors"
                onClick={() => onItemClick && onItemClick(name)}
              >
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <div className="w-6 h-6 relative rounded-sm flex-shrink-0 border border-muted overflow-hidden bg-background flex items-center justify-center">
                    {isBrandList ? (
                      <BrandLogo brandName={name} logoMap={brandLogoMap} />
                    ) : skipIndividualRequests ? (
                      // Show placeholder when individual requests are skipped
                      <svg 
                        className="w-3.5 h-3.5 mr-1.5 text-gray-400" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                        aria-label={`Placeholder for ${name} logo`}
                      >
                        <path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"></path>
                      </svg>
                    ) : (
                      <MediaOutletLogo outletName={name} logoUrl={logoUrl} />
                    )}
                  </div>
                  <span className="truncate font-medium" title={name}>{name}</span>
                </div>
                <span className="font-semibold text-muted-foreground ml-2">{formatNumber(count)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Not enough data.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default function Dashboard() {
  // Hydration safety
  const isHydrated = useHydrationSafe();
  
  // Auth context
  const { user, loading, signIn, error, clearError, getAllowedBrandIds, userAccess, canPerformAction } = useAuth() // Ensure useAuth is destructured
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const supabase = createClientComponentClient()
  
  const mountedRef = useRef(true); // Add this ref

  // Add state for detecting stuck loading - MOVED HERE FROM BELOW
  const [isLoadingStuck, setIsLoadingStuck] = useState(false);

  // Add useEffect for mountedRef
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Create a stable key instead of a random one
  const componentKey = useRef('dashboard-stable').current;
  
  // Page initialization state
  const [isLoading, setIsLoading] = useState(true)
  const [allClippings, setAllClippings] = useState<NewsClipping[]>([])
  
  // UI state management
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('Weekly')
  const [searchTerm, setSearchTerm] = useState('') 
  const [dateRange, setDateRange] = useState<{startDate?: string; endDate?: string}>({})

  
  // Sheet state
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [savedClipIds, setSavedClipIds] = useState<Set<string>>(new Set());
  
  // Dashboard data state - use persistent state for data that takes time to load
  const [stats, setStats] = usePersistentState(
    'dashboard_stats',
    { totalClippings: 0, averageSentiment: 0, sources: 0, totalReach: 0, monthlyVisitors: 0, visibilityPercentage: 0 },
    { syncAcrossTabs: true }
  )
  const [topSources, setTopSources] = usePersistentState<TopListItem[]>(
    'dashboard_top_sources',
    [],
    { syncAcrossTabs: true }
  )
  const [topBrands, setTopBrands] = usePersistentState<TopListItem[]>(
    'dashboard_top_brands',
    [],
    { syncAcrossTabs: true }
  )
  const [topBrandsReach, setTopBrandsReach] = usePersistentState<Array<{ name: string; reach: number }>>(
    'dashboard_top_brands_reach',
    [],
    { syncAcrossTabs: true }
  )
  const [overallSentimentData, setOverallSentimentData] = usePersistentState<SentimentCount[]>(
    'dashboard_sentiment_data',
    [],
    { syncAcrossTabs: true }
  )
  const [brandLogoMap, setBrandLogoMap] = usePersistentState<Record<string, string | null>>(
    'dashboard_brand_logo_map',
    {},
    { syncAcrossTabs: true }
  )
  const [sourceLogoMap, setSourceLogoMap] = usePersistentState<Record<string, string | null>>(
    'dashboard_source_logo_map',
    {},
    { syncAcrossTabs: true }
  )
  
  // Filter related state
  const { filters, handleFilterChange: hookHandleFilterChange, clearAllFilters: hookClearAllFilters, setFilters, isLoadingFilters } = useFilterState()
  const [filterOptions, setFilterOptions] = useState<Record<string, string[]>>({
    brandName: [], category: [], source: [], sentiment: [], id: [],
  })
  
  // Filter counts state for SideFilterSheet
  const [optionCounts, setOptionCounts] = useState<Record<string, Record<string, number>>>({})
  const [dynamicCounts, setDynamicCounts] = useState<Record<string, Record<string, number>>>({})
  
  // Stabilize filtered clippings with a ref to prevent constant re-renders
  const [filteredClippings, setFilteredClippings] = useState<NewsClipping[]>([])
  const filteredClippingsRef = useRef<NewsClipping[]>([])
  
  // Loading state with atomic granularity for components
  const [statsLoading, setStatsLoading] = useState(false)
  const [topBrandsLoading, setTopBrandsLoading] = useState(false)
  const [topBrandsReachLoading, setTopBrandsReachLoading] = useState(false)
  const [topSourcesLoading, setTopSourcesLoading] = useState(false)
  const [sentimentLoading, setSentimentLoading] = useState(false)
  const [clipsLoading, setClipsLoading] = useState(false)
  const [activityLoading, setActivityLoading] = useState(false)
  
  // Track if a data refresh is in progress
  const isProcessingRef = useRef(false)
  const filtersRef = useRef(filters)
  const searchTermRef = useRef(searchTerm)
  const hasProcessedRef = useRef(false)
  const defaultDateFilterApplied = useRef(false)
  const lastVisibleTime = useRef(Date.now())
  
  // Store last filter state to prevent unnecessary updates
  const lastFilterState = useRef<string>('{}')
  // Add a flag to track initial load
  const initialLoadComplete = useRef(false)
  
  // Define the stuck loading detection effect at the top level
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    // Only consider it stuck if we're loading AND initial load hasn't completed
    // This prevents false positives during normal filter operations
    if ((isLoading || isLoadingFilters) && !initialLoadComplete.current) {
      console.log(`Dashboard: Starting stuck detection timer. isLoading: ${isLoading}, isLoadingFilters: ${isLoadingFilters}, initialLoadComplete: ${initialLoadComplete.current}`);
      // If still loading after 8 seconds, consider it stuck
      timeoutId = setTimeout(() => {
        console.log('Dashboard loading state appears stuck');
        setIsLoadingStuck(true);
      }, 8000);
    } else {
      console.log(`Dashboard: Clearing stuck state. isLoading: ${isLoading}, isLoadingFilters: ${isLoadingFilters}, initialLoadComplete: ${initialLoadComplete.current}`);
      setIsLoadingStuck(false);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading, isLoadingFilters]);
  
  // Manual reload handler
  const handleForceReload = () => {
    console.log('Forcing dashboard reload');
    window.location.reload();
  };

  // Determine if auth should be bypassed (for development/testing)
  const isAuthBypassed = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('auth_bypass_active') === 'true' || (window as any).forceDashboardRender === true;
  }, []);

  // Initial data loading, runs only once when user logs in
  const loadDashboardData = useCallback(async () => {
    if (!user || !userAccess || isLoadingFilters) return; // Don't proceed if filters aren't ready
    
    // Don't run if already processing data
    if (isProcessingRef.current) {
      console.log('Dashboard: Already processing, skipping load');
      return;
    }
    
    // Add a timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      console.error('Dashboard: Load timeout after 30 seconds');
      if (mountedRef.current) {
        setIsLoading(false);
        setIsLoadingStuck(true);
      }
      isProcessingRef.current = false;
    }, 30000);
    
    try {
      console.log('Dashboard: Starting data load');
      isProcessingRef.current = true;
      if (mountedRef.current) setIsLoading(true);
      
      // Set all loading states at once
      if (mountedRef.current) {
        setStatsLoading(true);
        setTopBrandsLoading(true);
        setTopBrandsReachLoading(true);
        setTopSourcesLoading(true);
        setSentimentLoading(true);
        setClipsLoading(true);
        setActivityLoading(true);
      }
      
      // Fetch allowed brands and initialize filter
      const allowedBrandInfo = getAllowedBrandIds();
      let brandFilter: BrandAccessFilter;
      
      if (allowedBrandInfo === 'none') brandFilter = { type: 'none' };
      else if (allowedBrandInfo === 'all') brandFilter = { type: 'all' };
      else {
        const brandNamesToFilter = await fetchBrandNamesByIds(allowedBrandInfo);
        brandFilter = { type: 'specific', names: brandNamesToFilter };
      }
      
      // Fetch news clippings
      console.log('Dashboard: Fetching news clippings...');
      const clippings = await fetchNewsClippings(undefined, brandFilter);
      const safeClippings = clippings || [];
      console.log(`Dashboard: Fetched ${safeClippings.length} clippings`);
      
      // Set all clippings
      setAllClippings(safeClippings);
      
      // Extract filter options before applying filters
      console.log('Dashboard: Extracting filter options...');
      const options = extractFilterOptions(safeClippings);
      setFilterOptions(options);
      console.log('Dashboard: Filter options extracted');
      
      // Fetch brand logos - skip if tab was hidden during load for faster loading
      let brandsData = null;
      let brandsError = null;
      
      if (wasTabHiddenDuringOperation) {
        console.log('Dashboard: Skipping brand logos fetch due to tab switch during load');
        brandsError = new Error('Skipped due to tab switch');
      } else {
        console.log('Dashboard: Fetching brand logos...');
        try {
          // Add a shorter timeout for faster recovery
          const brandQueryPromise = supabase
            .from('Brands')
            .select('Name, logo_url, id')
            .order('Name');
          
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Brand query timeout after 5 seconds')), 5000);
          });
          
          const result = await Promise.race([brandQueryPromise, timeoutPromise]) as any;
          
          brandsData = result.data;
          brandsError = result.error;
          
          console.log('Dashboard: Brand query completed', { 
            dataLength: brandsData?.length, 
            error: brandsError 
          });
        } catch (err) {
          console.error('Dashboard: Exception during brand fetch:', err);
          brandsError = err;
        }
      }
      
      if (brandsError) {
        console.error('Error fetching brands:', brandsError);
        console.log('Dashboard: Continuing with empty brand logo map due to error');
        // Continue with empty logo map on error
      } else {
        console.log(`Dashboard: Successfully fetched ${brandsData?.length || 0} brands`);
      }
      
      // Build logo map
      console.log('Dashboard: Building brand logo map...');
      const newLogoMap: Record<string, string | null> = {};
      if (brandsData) {
        brandsData.forEach((brand: { Name?: string | null; logo_url?: string | null }) => {
          if (brand.Name) { 
            newLogoMap[brand.Name] = brand.logo_url || null;
          }
        });
      }
      setBrandLogoMap(newLogoMap);
      console.log(`Dashboard: Brand logo map built with ${Object.keys(newLogoMap).length} entries`);

      // Fetch source logos - skip if tab was hidden during load for faster loading
      let mediaOutletsData = null;
      let mediaOutletsError = null;
      
      if (wasTabHiddenDuringOperation) {
        console.log('Dashboard: Skipping source logos fetch due to tab switch during load');
        mediaOutletsError = new Error('Skipped due to tab switch');
      } else {
        console.log('Dashboard: Fetching source logos...');
        try {
          // Add a shorter timeout for faster recovery
          const sourceQueryPromise = supabase
            .from('media_outlets')
            .select('name, logo_url, domain')
            .not('name', 'is', null);
          
          const sourceTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Source logos query timeout after 5 seconds')), 5000);
          });
          
          const sourceResult = await Promise.race([sourceQueryPromise, sourceTimeoutPromise]) as any;
          
          mediaOutletsData = sourceResult.data;
          mediaOutletsError = sourceResult.error;
          
          console.log('Dashboard: Source query completed', { 
            dataLength: mediaOutletsData?.length, 
            error: mediaOutletsError 
          });
        } catch (err) {
          console.error('Dashboard: Exception during source fetch:', err);
          mediaOutletsError = err;
        }
      }

      if (mediaOutletsError) {
        console.error('Error fetching media outlets:', mediaOutletsError);
        console.log('Dashboard: Continuing with empty source logo map due to error');
      } else {
        console.log(`Dashboard: Successfully fetched ${mediaOutletsData?.length || 0} media outlets`);
      }

      // Build source logo map
      const newSourceLogoMap: Record<string, string | null> = {};
      if (mediaOutletsData) {
        mediaOutletsData.forEach((outlet: { name?: string | null; logo_url?: string | null; domain?: string | null }) => {
          if (outlet.name && outlet.logo_url) {
            newSourceLogoMap[outlet.name] = outlet.logo_url;
          }
          if (outlet.domain && outlet.logo_url) {
            const domain = outlet.domain.toLowerCase();
            newSourceLogoMap[domain] = outlet.logo_url;
            if (!domain.startsWith('www.') && !domain.startsWith('m.')) {
              newSourceLogoMap[`www.${domain}`] = outlet.logo_url;
              newSourceLogoMap[`m.${domain}`] = outlet.logo_url;
            } else if (domain.startsWith('www.')) {
              newSourceLogoMap[domain.replace('www.', 'm.')] = outlet.logo_url;
               newSourceLogoMap[domain.replace('www.', '')] = outlet.logo_url; // Add base domain
            } else if (domain.startsWith('m.')) {
              newSourceLogoMap[domain.replace('m.', 'www.')] = outlet.logo_url;
              newSourceLogoMap[domain.replace('m.', '')] = outlet.logo_url; // Add base domain
            }
          }
        });
      }
      setSourceLogoMap(newSourceLogoMap);
      console.log(`Dashboard: Source logo map built with ${Object.keys(newSourceLogoMap).length} entries`);

      // APPLY FILTERS IN A SINGLE STEP
      console.log('Dashboard: Applying initial filters', filters);
      const filteredData = applyDashboardFiltersUtil(safeClippings, searchTerm.trim(), filters);
      console.log(`Dashboard: Filtered data: ${filteredData.length} clips`);
      setFilteredClippings(filteredData);
      filteredClippingsRef.current = filteredData;
      
      // Process the dashboard data with filtered clippings and logo maps
      console.log('Dashboard: Processing dashboard data...');
      await processDashboardData(filteredData, newLogoMap, newSourceLogoMap, true);
      console.log('Dashboard: Dashboard data processing completed');
      
      console.log(`Dashboard: Initial load complete - showing ${filteredData.length} of ${safeClippings.length} clips`);
      initialLoadComplete.current = true;
      console.log('Dashboard: initialLoadComplete.current set to true');
      
      // Clear the timeout since we completed successfully
      clearTimeout(timeoutId);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      if (mountedRef.current) setIsLoading(false); // Guard this
      // Clear the timeout on error too
      clearTimeout(timeoutId);
    } finally {
      if (mountedRef.current) setIsLoading(false); // Guard this
      // Reset processing flag after a short delay
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 300);
    }
  }, [user, userAccess, getAllowedBrandIds, supabase, searchTerm, filters, isLoadingFilters]);

  // Unified data processing function - modified to avoid cascading timeouts
  const processDashboardData = useCallback(async (
    clippings: NewsClipping[],
    bLogoMap: Record<string, string | null>,
    sLogoMap: Record<string, string | null>,
    isInitialLoad: boolean = false
  ) => {
    if (isProcessingRef.current && !isInitialLoad) return;
    
    try {
      if (!isInitialLoad) {
        isProcessingRef.current = true;
        
        // Set all loading states at once instead of staggered timeouts
        if (mountedRef.current) {
          setStatsLoading(true);
          setTopBrandsLoading(true);
          setTopBrandsReachLoading(true);
          setTopSourcesLoading(true);
          setSentimentLoading(true);
          setClipsLoading(true);
          setActivityLoading(true);
        }
      }
      
      // Calculate basic stats
      const uniqueSources = new Set(clippings.map(clip => clip.source));
      const uniqueBrands = new Set(clippings.filter(clip => clip.brandName).map(clip => clip.brandName));
      
      // Calculate sentiment data
      let totalSentimentScore = 0;
      let countedClips = 0;
      const sentimentCounts: { [key: string]: number } = { 
        'Very Positive': 0, 
        'Positive': 0, 
        'Neutral': 0, 
        'Negative': 0, 
        'Very Negative': 0 
      };
      
      clippings.forEach(clip => {
        if (clip.sentiment) {
          const sentiment = clip.sentiment;
          let score = 0;
          
          if (sentiment === 'Very Positive') score = 5;
          else if (sentiment === 'Positive') score = 4;
          else if (sentiment === 'Neutral') score = 3;
          else if (sentiment === 'Negative') score = 2;
          else if (sentiment === 'Very Negative') score = 1;

          if (score > 0 && sentimentCounts.hasOwnProperty(sentiment)) {
            totalSentimentScore += score;
            countedClips++;
            sentimentCounts[sentiment] = (sentimentCounts[sentiment] || 0) + 1;
          }
        }
      });
      
      const averageSentiment = countedClips > 0 ? parseFloat((totalSentimentScore / countedClips).toFixed(1)) : 0;
      
      // Calculate top brands
      const ITEMS_TO_FETCH = 7;
      const brandCounts: Record<string, { count: number; logoUrl: string | null }> = {};
      
      clippings.forEach(clip => {
        if (clip.brandName) {
          if (!brandCounts[clip.brandName]) {
            brandCounts[clip.brandName] = { 
              count: 1, 
              logoUrl: bLogoMap[clip.brandName] || null 
            };
          } else {
            brandCounts[clip.brandName].count++;
            // Ensure logoUrl is preserved/updated even when incrementing
            if (!brandCounts[clip.brandName].logoUrl && bLogoMap[clip.brandName]) {
              brandCounts[clip.brandName].logoUrl = bLogoMap[clip.brandName];
            }
          }
        }
      });
      
      const sortedBrands: TopListItem[] = Object.entries(brandCounts)
        .map(([name, data]) => ({
          name,
          count: data.count,
          logoUrl: data.logoUrl
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, ITEMS_TO_FETCH);
      
      // Calculate brand reach totals (only from Main role clips to match Total Reach stat)
      // Note: This should use the filtered clippings to respect current brand filters
      console.log('Dashboard: Processing reach data from', clippings.length, 'filtered clippings');
      const brandReachCounts: Record<string, number> = {};
      
      clippings.forEach(clip => {
        if (clip.brandName && clip.reach && clip.reach > 0 && 
            clip.visibility && clip.visibility.toLowerCase().includes('main')) {
          brandReachCounts[clip.brandName] = (brandReachCounts[clip.brandName] || 0) + clip.reach;
          console.log('Dashboard: Adding reach for brand:', clip.brandName, 'reach:', clip.reach);
        }
      });
      
      console.log('Dashboard: Brand reach counts:', brandReachCounts);
      
      // If there are no brands with reach data (e.g., when filtered to a brand with no reach), 
      // return empty array to show "No reach data available"
      const sortedBrandsReach = Object.entries(brandReachCounts).length > 0 
        ? Object.entries(brandReachCounts)
            .map(([name, reach]) => ({ name, reach }))
            .sort((a, b) => b.reach - a.reach)
            .slice(0, ITEMS_TO_FETCH)
        : [];
      
      console.log('Dashboard: Final sorted brands reach:', sortedBrandsReach);
      
      // Calculate top sources
      const sourceCounts: Record<string, number> = {};
      
      clippings.forEach(clip => {
        if (clip.source) {
          sourceCounts[clip.source] = (sourceCounts[clip.source] || 0) + 1;
        }
      });
      
      const sortedSources: TopListItem[] = Object.entries(sourceCounts)
        .map(([name, count]) => ({
          name,
          count,
          logoUrl: sLogoMap[name] || null
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, ITEMS_TO_FETCH);
      
      // Prepare sentiment chart data
      const sentimentChartData: SentimentCount[] = Object.entries(sentimentCounts)
        .map(([sentiment, count]) => ({
          sentiment,
          count,
          color: getSentimentColorValue(sentiment)
        }));
      
      // Calculate total reach and monthly visitors
      let totalReach = 0; // Sum of reach from Main role clips only
      let totalVisitors = 0; // Original visitors before reach calculation formula
      let mainRoleClips = 0;
      
      clippings.forEach(clip => {
        // Calculate original visitors by reversing the reach formula
        // Formula: reach = visitors * 0.1 * media_type_multiplier * visibility_multiplier
        // So: visitors = reach / (0.1 * media_type_multiplier * visibility_multiplier)
        if (clip.reach && clip.reach > 0) {
          // For simplified calculation, assuming multipliers average to 1
          // So visitors ≈ reach / 0.1 = reach * 10
          const estimatedVisitors = clip.reach / 0.1;
          totalVisitors += estimatedVisitors;
        }
        
        // Count Main role clips and calculate total reach from Main role clips only
        if (clip.visibility && clip.visibility.toLowerCase().includes('main')) {
          mainRoleClips++;
          // Only add to total reach if this is a Main role clip
          if (clip.reach && clip.reach > 0) {
            totalReach += clip.reach;
          }
        }
      });
      
      // Calculate visibility percentage (Main role clips out of total clips)
      const visibilityPercentage = clippings.length > 0 ? Math.round((mainRoleClips / clippings.length) * 100) : 0;
      
      // Update all the stats
      const stats = { 
        totalClippings: clippings.length, 
        averageSentiment, 
        sources: uniqueSources.size, 
        totalReach,
        monthlyVisitors: totalVisitors,
        visibilityPercentage
      };
      
      // Update all states at once instead of staggered timers
      if (mountedRef.current) {
        console.log('Dashboard: Setting topBrands data:', sortedBrands);
        console.log('Dashboard: Setting topBrandsReach data:', sortedBrandsReach);
        setStats(stats);
        setTopBrands(sortedBrands);
        setTopSources(sortedSources);
        setOverallSentimentData(sentimentChartData);
        setTopBrandsReach(sortedBrandsReach);
      }
      
      // Turn off all loading states at once
      if (mountedRef.current) {
        setStatsLoading(false);
        setTopBrandsLoading(false);
        setTopBrandsReachLoading(false);
        setTopSourcesLoading(false);
        setSentimentLoading(false);
        setClipsLoading(false);
        setActivityLoading(false);
      }
      
    } catch (error) {
      console.error('Error processing dashboard data:', error);
      // Reset all loading states immediately
      if (mountedRef.current) {
        setStatsLoading(false);
        setTopBrandsLoading(false);
        setTopBrandsReachLoading(false);
        setTopSourcesLoading(false);
        setSentimentLoading(false);
        setClipsLoading(false);
        setActivityLoading(false);
      }
    } finally {
      // Reset processing flag if not initial load
      if (!isInitialLoad) {
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 300);
      }
    }
  }, [mountedRef]);
  
  // Load data on initial render - ONLY ONCE
  useEffect(() => {
    // Prevent multiple loads - only load if user exists, filters are ready, initial load not complete, and not already processing
    if (user && !isLoadingFilters && !initialLoadComplete.current && !isProcessingRef.current) {
      // Apply default date filter ONLY if no date range is present in filters yet
      if (!filters.startDate && !filters.endDate && !defaultDateFilterApplied.current) {
        const today = new Date();
        const oneYearAgo = new Date(today);
        oneYearAgo.setDate(today.getDate() - 365);
        
        const startDateString = oneYearAgo.toISOString().split('T')[0];
        const endDateString = today.toISOString().split('T')[0];
        
        // Set the default date range in the filters state
        // Using setFilters directly to update the hook state
        setFilters(prevFilters => ({
          ...prevFilters,
          startDate: [startDateString],
          endDate: [endDateString]
        }));
        setDateRange({ startDate: startDateString, endDate: endDateString }); // Update local state for UI
        defaultDateFilterApplied.current = true; // Mark as applied
        console.log('Dashboard: Applied default 365-day date filter');
        // Don't call loadDashboardData yet, let the filter change effect handle it
        return; 
      }
      
      // If default date filter is already applied or not needed, proceed with loading
      console.log('Dashboard: Initial data load triggered (or proceeding after default date filter)');
      loadDashboardData();
    } else if (initialLoadComplete.current) {
      console.log('Dashboard: Initial load already complete, skipping');
    } else if (isProcessingRef.current) {
      console.log('Dashboard: Already processing data, skipping');
    }
  }, [user, loadDashboardData, isLoadingFilters, filters, setFilters]); // Added filters and setFilters as dependencies
  
  // Handle pathname changes without triggering loading states
  useEffect(() => {
    // Only execute if we're on the dashboard page, have a user, and initial load is complete
    if (pathname === '/dashboard' && user && !isLoadingFilters && initialLoadComplete.current && !isProcessingRef.current) {
      // Apply current filters to make sure UI is in sync - but don't reload all data
      console.log('Dashboard: Refreshing filtered data after navigation');
      const filtered = applyDashboardFiltersUtil(allClippings, searchTerm.trim(), filters);
      
      // Only update if there's actually a difference in the filtered results
      const currentIds = filteredClippingsRef.current.map(f => f.id).sort().join(',');
      const newIds = filtered.map(f => f.id).sort().join(',');
      
      if (currentIds !== newIds) {
        setFilteredClippings(filtered);
        filteredClippingsRef.current = filtered;
        processDashboardData(filtered, brandLogoMap, sourceLogoMap);
      }
    }
  }, [pathname, user, isLoadingFilters, processDashboardData, allClippings, filters, searchTerm, brandLogoMap, sourceLogoMap]);
  
  // Use the new tab visibility hook for intelligent tab switching detection
  const { wasTabHiddenDuringOperation } = useTabVisibility({
    onTabReturn: (timeHidden) => {
      console.log(`Dashboard: Tab returned after ${timeHidden}ms`);
      // Only take action if initial load is not complete and we're not already processing
      if (!initialLoadComplete.current && !isProcessingRef.current) {
        console.log('Dashboard: Tab returned during initial load, but not taking aggressive action');
        // Don't reload data, just let the existing loading continue
      }
    },
    onTabHidden: () => {
      console.log('Dashboard: Tab hidden');
      // Just log, don't take any aggressive action
    },
    minHiddenTimeForAction: 3000, // Only consider it significant if hidden for 3+ seconds
    debounceMs: 500 // Short debounce to prevent rapid firing
  });
  
  // Simplified filter change handler with improved debounce
  useEffect(() => {
    // Store the current filters for comparison
    filtersRef.current = filters;
    searchTermRef.current = searchTerm;
    
    // Generate a key for current filter state to check for changes
    const currentFilterState = JSON.stringify({ filters, searchTerm });
    
    // Skip update if nothing changed OR if initial load is not complete OR if allClippings is empty
    if (currentFilterState === lastFilterState.current || !initialLoadComplete.current || !allClippings || allClippings.length === 0) {
      // If no clippings, ensure displayed data is also empty
      if ((!allClippings || allClippings.length === 0) && initialLoadComplete.current) {
        setFilteredClippings([]);
        filteredClippingsRef.current = [];
        // Call processDashboardData with empty clippings to update stats and cards to show "No data"
        processDashboardData([], brandLogoMap, sourceLogoMap);
        console.log('Dashboard: No clippings available, cleared filtered data and updated components.');
      }
      return;
    }
    
    // Update last filter state
    lastFilterState.current = currentFilterState;
    
    // Debounce filter applications with a longer timeout (300ms instead of 200ms)
    const timerId = setTimeout(() => {
      if (isProcessingRef.current) return;
      
      console.log('Dashboard: Processing filter changes');
      
      // Apply filters to get the new filtered list
      const filtered = applyDashboardFiltersUtil(allClippings, searchTerm.trim(), filters);
      
      // Only update if the filter actually changed the result set
      // or if the filtered result is empty (to ensure UI updates to "no results")
      if (JSON.stringify(filtered.map(f => f.id)) !== JSON.stringify(filteredClippingsRef.current.map(f => f.id)) || filtered.length === 0) {
        // Update filtered clippings reference immediately
        filteredClippingsRef.current = filtered;
        setFilteredClippings(filtered);
        
        // Process dashboard data with the new filtered list and existing logo maps
        processDashboardData(filtered, brandLogoMap, sourceLogoMap);
      }
    }, 300);
    
    return () => clearTimeout(timerId);
  }, [filters, searchTerm, allClippings, brandLogoMap, sourceLogoMap, processDashboardData]);

  // Calculate filter counts whenever all clippings or filtered clippings change
  useEffect(() => {
    if (allClippings.length > 0) {
      console.log("[Dashboard] Generating filter counts from", allClippings.length, "clippings");
      
      // Generate option counts from all clippings (for display in filter dropdowns)
      const categoryFieldMap = {
        brand: 'brandName' as keyof NewsClipping,
        sentiment: 'sentiment' as keyof NewsClipping,
        category: 'category' as keyof NewsClipping,
        source: 'source' as keyof NewsClipping
      };
      
      const optionCountsData = generateFilterCounts(allClippings, categoryFieldMap);
      setOptionCounts(optionCountsData);
      
      // Generate dynamic counts based on current filters (cross-filter counts)
      const fetchDynamicCounts = async () => {
        try {
          console.log("[Dashboard] Generating dynamic counts with filters:", filters);
          
          // Get the brand access filter for the current user
          const allowedBrandInfo = getAllowedBrandIds();
          let brandFilter: BrandAccessFilter;
          
          if (allowedBrandInfo === 'none') brandFilter = { type: 'none' };
          else if (allowedBrandInfo === 'all') brandFilter = { type: 'all' };
          else {
            const brandNamesToFilter = await fetchBrandNamesByIds(allowedBrandInfo);
            brandFilter = { type: 'specific', names: brandNamesToFilter };
          }
          
          const dynamicCountsData = await getDynamicFilterCounts(filters, brandFilter);
          setDynamicCounts(dynamicCountsData);
        } catch (error) {
          console.error("[Dashboard] Error generating dynamic counts:", error);
          setDynamicCounts({});
        }
      };
      
      fetchDynamicCounts();
    } else {
      // Clear counts if no data
      setOptionCounts({});
      setDynamicCounts({});
    }
  }, [allClippings, filters, getAllowedBrandIds]);

  // Original handler that updates the main filters state (expects full object)
  const handleFilterStateUpdate = (newFilters: Record<string, string[]>) => {
    setFilters(newFilters);
    updateURLWithFilters(newFilters);
  }

  // Update URL with current filters using History API to avoid router navigation
  const updateURLWithFilters = (currentFilters: Record<string, string[]>) => {
    // Create a new URLSearchParams object from the current URL
    const params = new URLSearchParams(window.location.search);
    
    // Define all known filter keys that should be managed
    const knownFilterKeys = ['sentiment', 'category', 'brandName', 'source', 'visibility', 'startDate', 'endDate', 'id', 'ids'];
    
    // First, remove all existing filter parameters to ensure clean state
    knownFilterKeys.forEach(key => {
      params.delete(key);
    });
    
    // Then add back only the filters that have values
    for (const [field, values] of Object.entries(currentFilters)) {
      if (values && values.length > 0) {
        if (field === 'startDate' || field === 'endDate') {
          // Handle date range fields specially (just use the first value)
          params.set(field, values[0]);
        } else if (field === 'id') {
          // For ID filters, use the 'ids' parameter with comma-separated values
          params.set('ids', values.join(','));
        } else {
          // For all other filter types, always use comma-separated list for ALL values
          params.set(field, values.join(','));
        }
      }
    }
    
    // Use History API instead of router.replace to avoid triggering navigation
    const newUrl = `${pathname}?${params.toString()}`;
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', newUrl);
    }
  };
  
  // NEW: Wrapper function to match SideFilterBar's expected prop signature
  const handleFilterChangeForSideBar = (field: string, value: string, checked: boolean) => {
    // Create a new filters object based on the current state
    const newFilters = { ...filters }; 
    
    // Initialize the field array if it doesn't exist
    if (!newFilters[field]) {
      newFilters[field] = [];
    }
    
    // Add or remove the value based on the 'checked' state
    if (checked) {
      if (!newFilters[field].includes(value)) {
        newFilters[field] = [...newFilters[field], value];
      }
    } else {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      // Remove the key if the array becomes empty
      if (newFilters[field].length === 0) {
        delete newFilters[field];
      }
    }
    
    // Update the main filter state using the original handler (now includes URL update)
    handleFilterStateUpdate(newFilters);
  };

  // Handle search change
  const handleSearchChange = (term: string) => {
    setSearchTerm(term)
  }

  // Date range change handler (called by ActivityGraph clicks)
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    const newFilters = { ...filters, startDate: [startDate], endDate: [endDate] };
    handleFilterStateUpdate(newFilters);
    setDateRange({ startDate, endDate });
    console.log("Dashboard: Date range changed via ActivityGraph", { startDate, endDate });
  };



  // Clear all filters
  const clearAllFilters = () => {
    hookClearAllFilters();
    setSearchTerm('');
    setDateRange({});
    setSelectedPeriod('Weekly');
    defaultDateFilterApplied.current = false;
    
    // Update URL to remove all filter parameters using History API
    const newUrl = pathname; // No query params for cleared filters
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', newUrl);
    }
    
    console.log("Dashboard: All filters cleared and URL updated");
  };



  // Handle opening the clip details sheet
  const handleOpenSheet = (clipId: string) => {
    setSelectedClipId(clipId);
    setIsSheetOpen(true);
  };

  // Handle navigating to media clips with filter
  const handleFilterItemClick = (type: string, value: string) => {
    // Use router to navigate to media-clips page with the filter as URL parameter
    router.push(`/media-clips?${type}=${encodeURIComponent(value)}`);
  };

  // Initial loading state or no user
  if (loading || !user) {
    return <AuthCheck />; // AuthCheck handles loading/redirect
  }

  // Show loading state only during initial load - not for subsequent filter changes
  // This prevents the loading screen from appearing when switching tabs
  const shouldShowFullScreenLoader = (isLoading || isLoadingFilters) && !initialLoadComplete.current;
  
  if (shouldShowFullScreenLoader) {
    return (
      <MainLayout title="Dashboard">
        <div className="flex min-h-screen items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isLoadingFilters ? "Initializing filters..." : "Loading dashboard data..."}
              </p>
            </div>
            
            {/* Show reload button when loading is stuck */}
            {isLoadingStuck && (
              <Button 
                onClick={handleForceReload}
                className="mt-2 bg-red-500 hover:bg-red-600 text-white"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Dashboard Stuck - Click to Reload
              </Button>
            )}
          </div>
        </div>
      </MainLayout>
    );
  }

  // Main Dashboard Render
  return (
    <>
      <AuthCheck />
      {/* Conditionally render dashboard content only if user exists OR bypass is active AND hydrated */}
      {(user || isAuthBypassed) && isHydrated && (
        <MainLayout title="Dashboard">
          {/* Mobile Filter Bar */}
          <div className="mobile-filter-container">
            <MobileFilterBar
              filters={filters}
              filterOptions={filterOptions}
              handleFilterChange={handleFilterChangeForSideBar}
              clearAllFilters={clearAllFilters}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              clearDateRange={() => {
                setDateRange({});
                const newFilters = { ...filters };
                delete newFilters.startDate;
                delete newFilters.endDate;
                handleFilterStateUpdate(newFilters);
              }}
              filteredCount={filteredClippings.length}
              hideCreateNew={true}
              showViewSelector={false}
            />
          </div>
          
          {/* Main flex container with sidebar and content */}
          <div className="flex h-full">
            {/* Sidebar - Hidden on mobile */}
            <div className="hidden lg:block">
              <SideFilterSheet
                isVisible={true}
                filters={filters}
                filterOptions={filterOptions}
                handleFilterChange={handleFilterChangeForSideBar}
                clearAllFilters={clearAllFilters}
                savedFilters={{}}
                currentFilterName=""
                newFilterName=""
                setNewFilterName={() => {}}
                saveCurrentFilter={() => {}}
                loadSavedFilter={() => {}}
                setDefaultFilter={() => {}}
                deleteSavedFilter={() => {}}
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                clearDateRange={() => {
                  setDateRange({});
                  const newFilters = { ...filters };
                  delete newFilters.startDate;
                  delete newFilters.endDate;
                  handleFilterStateUpdate(newFilters);
                }}
                filteredCount={filteredClippings.length}
                optionCounts={optionCounts}
                dynamicCounts={dynamicCounts}
                hideCreateNew={true}
                showViewSelector={false}
              />
            </div>

            {/* Main content area */}
            <div className="flex-1 flex flex-col min-w-0 bg-white min-h-0">
              {/* Content area with dashboard widgets */}
              <div className="flex-1 overflow-auto p-4 pb-4 lg:pb-4 mobile-content-padding">
                {/* Conditionally render Loader or Content based on loading state */}
                {((isLoading || isLoadingFilters) && !initialLoadComplete.current) ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {isLoadingFilters ? 'Loading filters...' : 'Loading dashboard data...'}
                      </span>
                      {/* Show reload button when loading is stuck */}
                      {isLoadingStuck && (
                        <Button 
                          onClick={handleForceReload}
                          className="mt-2 bg-red-500 hover:bg-red-600 text-white"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Dashboard Stuck - Click to Reload
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="max-w-[1600px] mx-auto">
                    <style jsx global>{`
                      @media (max-width: 768px) {
                        /* Hide search input */
                        .dashboard-page-search-form {
                          display: none !important;
                        }
                        
                        /* Aggressively hide ALL text labels in Activity graph */
                        .mobile-optimized-graph span:not(.sr-only),
                        .mobile-optimized-graph div > span,
                        .mobile-optimized-graph label,
                        .mobile-optimized-graph [data-tip],
                        .mobile-optimized-graph div[role="tooltip"],
                        .mobile-optimized-graph .graph-label,
                        .recharts-legend-item-text {
                          display: none !important;
                        }

                        /* Only show selector dropdowns and buttons */
                        .mobile-optimized-graph select {
                          margin: 0 1px !important;
                          padding: 2px !important;
                          height: 30px !important;
                          border-radius: 4px !important;
                          min-width: 0 !important;
                          width: auto !important;
                        }
                        
                        /* Make all controls extremely compact */
                        .mobile-optimized-graph .controls-container,
                        .mobile-optimized-graph .flex {
                          gap: 2px !important;
                          padding: 0 !important;
                          margin: 0 !important;
                          flex-wrap: nowrap !important;
                          justify-content: center !important;
                        }
                        
                        /* Force chart container to take more space */
                        .mobile-optimized-graph .chart-container {
                          height: 320px !important;
                          margin-top: 0 !important;
                          width: 100% !important;
                        }
                        
                        /* Reduce chart margins and padding for desktop */
                        .mobile-optimized-graph .recharts-wrapper {
                          margin-bottom: -10px !important;
                        }
                        
                        .mobile-optimized-graph {
                          height: 100% !important;
                          display: flex !important;
                          flex-direction: column !important;
                        }
                        
                        /* Make chart take full available height */
                        .mobile-optimized-graph > div {
                          flex: 1 !important;
                          min-height: 0 !important;
                        }
                        
                        /* Sentiment chart positioning */
                        .sentiment-card .pie-chart-container {
                          transform: translateY(-40px) scale(1.1) !important;
                        }

                        /* Remove excessive bottom margins on mobile */
                        .dashboard-content {
                          margin-bottom: 0 !important;
                          padding-bottom: 1rem !important;
                        }
                      }
                    `}</style>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-4 mb-4">
                      <KeyMetricsCard 
                        stat={statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : formatNumber(stats.totalClippings)} 
                        label="Total Clippings" 
                        icon={<Newspaper className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />} 
                      />
                      <KeyMetricsCard 
                        stat={statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : `${stats.visibilityPercentage}%`} 
                        label="Visibility (Main role)" 
                        icon={<BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />} 
                      />
                      <KeyMetricsCard 
                        stat={statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : formatNumber(stats.sources)} 
                        label="Unique Sources" 
                        icon={<Users className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />} 
                      />
                      <KeyMetricsCard 
                        stat={statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (stats.averageSentiment > 0 ? stats.averageSentiment.toFixed(1) : 'N/A')} 
                        label="Avg. Sentiment" 
                        icon={<Smile className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />}
                      />
                    </div>
                    
                    {/* Additional Stats Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-4 mb-4">
                      <KeyMetricsCard 
                        stat={statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : formatNumber(stats.totalReach)} 
                        label="Total Reach" 
                        icon={<BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />} 
                      />
                      <KeyMetricsCard 
                        stat={statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : formatNumber(stats.monthlyVisitors)} 
                        label="Monthly Visitors" 
                        icon={<Users className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />} 
                      />
                    </div>

                    {/* Main Content Row - Share of Voice, Activity Graph, and Share of Voice Reach */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-4 mb-4"> 
                      {/* Share of Voice Articles on the left side */}
                      <div className="lg:col-span-3">
                        {topBrandsLoading ? (
                          <Card className="shadow h-full flex items-center justify-center p-6">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </Card>
                        ) : (
                          <Card className="shadow h-full">
                            <CardHeader className="p-3 sm:p-4">
                              <CardTitle className="text-base font-medium">Share of Voice - Articles</CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 sm:p-4 pt-0 sm:pt-0 h-[300px]">
                              <ShareOfVoiceTreemap 
                                data={topBrands.map(brand => ({ name: brand.name, count: brand.count }))}
                                className="h-full"
                              />
                            </CardContent>
                          </Card>
                        )}
                      </div>
                      
                      {/* Activity Graph in the middle */} 
                      <Card className="lg:col-span-6 shadow h-full">
                        <CardHeader className="p-2 sm:p-3 pb-0 flex flex-row items-center justify-between">
                          <CardTitle className="text-base font-medium">Media Clips Activity</CardTitle>
                        </CardHeader>
                        <CardContent className="h-[360px] sm:h-[330px] md:h-[360px] p-0 pt-2 pb-0 px-2 relative overflow-hidden"> 
                          {isLoading || activityLoading ? (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            </div>
                          ) : filteredClippings.length > 0 ? (
                            <div className="mobile-optimized-graph h-full">
                              <ActivityGraph
                                clippings={filteredClippings}
                                selectedPeriod={selectedPeriod}
                                onPeriodChange={(period) => {
                                  console.log("Period changed to:", period);
                                  setSelectedPeriod(period);
                                }}
                                dashboardFilters={true}
                                activeDashboardFilters={filters}
                                key={`activity-graph-${selectedPeriod}-${filteredClippings.length}-${Object.keys(filters).length}-${Date.now()}`}
                                showAllCategories={true}
                                dashboardMode={true}
                              />
                            </div>
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <p className="text-muted-foreground">No media clips found matching your filters</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Share of Voice Reach on the right side */}
                      <div className="lg:col-span-3">
                        {topBrandsReachLoading ? (
                          <Card className="shadow h-full flex items-center justify-center p-6">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </Card>
                        ) : (
                          <Card className="shadow h-full">
                            <CardHeader className="p-3 sm:p-4">
                              <CardTitle className="text-base font-medium">Share of Voice - Reach</CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 sm:p-4 pt-0 sm:pt-0 h-[300px]">
                              <ShareOfVoiceReach 
                                data={topBrandsReach}
                                brandOrder={topBrands.map(brand => ({ name: brand.name, count: brand.count }))}
                                className="h-full"
                              />
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </div>

                    {/* Second Row - Sentiment Analysis, Latest Clips and Top Sources */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-4 mb-4">
                      {/* Latest Clips - MOVED TO THE LEFT */}
                      <div className="lg:col-span-6">
                        {clipsLoading ? (
                          <Card className="shadow h-full flex items-center justify-center p-6">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </Card>
                        ) : (
                          <LatestClipsCard 
                            clips={filteredClippings} 
                            onClipClick={handleOpenSheet}
                            brandLogoMap={brandLogoMap}
                            skipIndividualRequests={wasTabHiddenDuringOperation}
                          />
                        )}
                      </div>

                      {/* Sentiment Analysis - MOVED TO THE MIDDLE */}
                      <div className="lg:col-span-3">
                        {sentimentLoading ? (
                          <Card className="shadow h-full flex items-center justify-center p-6">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </Card>
                        ) : (
                          <SentimentAnalysisCard 
                            sentimentData={overallSentimentData} 
                          />
                        )}
                      </div>
                      
                      {/* Top Sources - REMAINS ON THE RIGHT */}
                      <div className="lg:col-span-3">
                        {topSourcesLoading ? (
                          <Card className="shadow h-full flex items-center justify-center p-6">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                          </Card>
                        ) : (
                          <TopListCard 
                            title="Top Sources" 
                            items={topSources} 
                            showViewAll={false}
                            onItemClick={(source) => handleFilterItemClick('source', source)}
                            brandLogoMap={brandLogoMap}
                            skipIndividualRequests={wasTabHiddenDuringOperation}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Clip Details Sheet */}
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetContent className="sm:max-w-xl w-full overflow-y-auto bg-white p-0">
                  {/* <SheetHeader>
                    <SheetTitle>Clip Details</SheetTitle>
                    <SheetDescription>Detailed information about the selected news clipping.</SheetDescription>
                  </SheetHeader> */}
                  {/* Add visually hidden title for accessibility like in media-clips page */}
                  <SheetTitle className="sr-only">Clip Details</SheetTitle> 
                  {selectedClipId && (
                    <ClipDetails
                      clipId={selectedClipId}
                      onClose={() => setIsSheetOpen(false)}
                      isSavedInitial={savedClipIds.has(selectedClipId)} 
                      onToggleSave={async (clipId: string) => { 
                        setSavedClipIds(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(clipId)) {
                            newSet.delete(clipId);
                          } else {
                            newSet.add(clipId);
                          }
                          return newSet;
                        });
                      }}
                      canDelete={canPerformAction('media_clippings', 'delete')} 
                    />
                  )}
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </MainLayout>
      )}
    </>
  );
}